/**
 * Minimal JWT helpers for client-side expiry checks.
 *
 * We intentionally do not verify signatures here; this is only used to avoid
 * sending obviously-expired tokens (e.g. Microsoft OBO assertions) and to
 * trigger a re-auth flow proactively.
 */

const base64UrlDecode = (input: string): string => {
  const normalized = input.replaceAll('-', '+').replaceAll('_', '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  return atob(normalized + padding);
};

/** Decode the JWT payload segment without signature verification. Returns null on malformed input. */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    return JSON.parse(base64UrlDecode(parts[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const getJwtExpSeconds = (token: string): number | null => {
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1])) as { exp?: unknown };
    const exp = payload?.exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
};

export const isJwtExpiredOrExpiringSoon = (
  token: string,
  options: { nowSeconds?: number; skewSeconds?: number } = {},
): boolean => {
  const nowSeconds = options.nowSeconds ?? Math.floor(Date.now() / 1000);
  const skewSeconds = options.skewSeconds ?? 120;
  const exp = getJwtExpSeconds(token);
  if (exp === null) {
    return false;
  }
  return exp <= nowSeconds + Math.max(0, skewSeconds);
};
